// Buttons (UX02, UX03). Owner: WP04. Lime primary is reserved for the one main action in a view.
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { LoaderCircle } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
type Common = { variant?: ButtonVariant; size?: ButtonSize; icon?: ReactNode; block?: boolean };

const cls = (variant: ButtonVariant, size: ButtonSize, block?: boolean, extra?: string) =>
  ['ui-btn', `ui-btn--${variant}`, `ui-btn--${size}`, block ? 'ui-btn--block' : '', extra ?? ''].filter(Boolean).join(' ');

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & Common & { loading?: boolean };

/** Text button. `loading` disables it and announces busy state; the label stays visible. */
export function Button({ variant = 'secondary', size = 'md', icon, block, loading, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} className={cls(variant, size, block, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <LoaderCircle className="ui-spin" aria-hidden size={18} /> : icon ? <span className="ui-btn__icon" aria-hidden>{icon}</span> : null}
      {children}
    </button>
  );
}

/** Navigation styled as a button (use for "go somewhere", Button for "do something"). */
export function ButtonLink({ variant = 'secondary', size = 'md', icon, block, className, children, ...rest }: LinkProps & Common) {
  return (
    <Link className={cls(variant, size, block, className)} {...rest}>
      {icon ? <span className="ui-btn__icon" aria-hidden>{icon}</span> : null}
      {children}
    </Link>
  );
}

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  /** Accessible name (required: icon-only controls must be labelled). */
  label: string;
  icon: ReactNode;
  variant?: 'ghost' | 'secondary' | 'overlay';
  /** Toggle state, exposed as aria-pressed. */
  pressed?: boolean;
  size?: 'sm' | 'md';
};

/** Icon-only button with a 44 px hit target. */
export function IconButton({ label, icon, variant = 'ghost', pressed, size = 'md', className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      className={['ui-iconbtn', `ui-iconbtn--${variant}`, `ui-iconbtn--${size}`, pressed ? 'is-pressed' : '', className ?? ''].filter(Boolean).join(' ')}
      {...rest}
    >
      <span aria-hidden>{icon}</span>
    </button>
  );
}
